import { CSSProperties, ForwardedRef, SyntheticEvent, forwardRef, useContext } from 'react'
import Artboard, { GIF, Image, Sprite, Text } from '../../artboard-gl'
import {
    ArtboardGifLayerProps,
    ArtboardImageLayerProps,
    ArtboardRef,
    ArtboardTextLayerProps,
} from '../../artboard-gl/types'
import { UGEContext } from '../context'
import { UGELayer } from '../types'

type Props = {
    className?: string
    fps?: number
    style?: CSSProperties
    onContextMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) => void
    onEllipsisMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) => void
}

const UGEArtboardBoard = (
    { className, fps, style, onContextMenu, onEllipsisMenu }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const {
        dimensions,
        duration,
        enableStickers,
        filter,
        filterIntensity,
        layers,
        paused,
        reversed,
        sourceSprite,
        trim,
        openEditor,
    } = useContext(UGEContext)

    function onGifDoubleClick(layer: UGELayer) {
        if (layer.searchTerm) {
            openEditor('stickers', { searchTerm: layer.searchTerm })
        }
    }

    function onTextDoubleClick(layer: UGELayer) {
        openEditor('captions', { layerId: layer.id })
    }

    return (
        <Artboard
            className={className}
            duration={duration}
            filter={filter}
            filterIntensity={filterIntensity}
            fps={fps}
            height={dimensions.height}
            key={`uge-artboard-transparency=${enableStickers}`}
            paused={paused}
            ref={ref}
            style={style}
            transparency={enableStickers}
            trim={trim}
            width={dimensions.width}
        >
            <Sprite height={dimensions.height} reversed={reversed} source={sourceSprite} width={dimensions.width} />
            {layers.map((layer) => {
                const { props, type } = layer
                const defaultProps = {
                    draggable: true,
                    key: `uge-artboard-layer-${layer.id}`,
                    x: Math.round(dimensions.width / 2 - props.width / 2),
                    onContextMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>) => onContextMenu(e, layer),
                    onEllipsisMenu: (e: SyntheticEvent<HTMLElement, MouseEvent>) => onEllipsisMenu(e, layer),
                }

                switch (type) {
                    case 'gif':
                    case 'sticker':
                        return (
                            <GIF
                                {...defaultProps}
                                {...(props as ArtboardGifLayerProps)}
                                onDoubleClick={() => onGifDoubleClick(layer)}
                            />
                        )
                    case 'image':
                        return <Image {...defaultProps} {...(props as ArtboardImageLayerProps)} />
                    case 'text':
                        return (
                            <Text
                                {...defaultProps}
                                {...(props as ArtboardTextLayerProps)}
                                onDoubleClick={() => onTextDoubleClick(layer)}
                            />
                        )
                    default:
                        return null
                }
            })}
        </Artboard>
    )
}

export default forwardRef(UGEArtboardBoard)
